import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Wrench, Hammer, Settings, Zap, Factory, CheckCircle, ChevronRight } from 'lucide-react';
import { t } from '../utils/translation';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import PageHeader from '../components/PageHeader';
import SectionSplitter from '../components/SectionSplitter';
import connectorMfg from '../assets/manufacturing/connector-mfg.jpg';
import stampingServices from '../assets/manufacturing/stamping-services.jpg';
import moldDevelopment from '../assets/manufacturing/mold-development.jpg';
import injectionMolding from '../assets/manufacturing/injection-molding.jpg';
import cableAssembly from '../assets/products/cable-assembly.jpg';
import qualityControl from '../assets/factory/quality-control.jpg';
import { useSiteAssets } from '../context/SiteAssetsContext';

const Services = ({ theme, language }) => {
  const { assets } = useSiteAssets();
  const services = [
    {
      icon: <Wrench className={`w-12 h-12 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      title: t('connectorManufacturing', language),
      desc: t('connectorManufacturingDesc', language),
      features: [
        t('customConnectorDesign', language),
        t('materialSelection', language),
        t('precisionManufacturing', language),
        t('qualityAssurance', language)
      ],
      image: assets.services_connector || connectorMfg
    },
    {
      icon: <Hammer className={`w-12 h-12 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      title: t('stampingServices', language),
      desc: t('stampingServicesDesc', language),
      features: [
        t('progressiveDie', language),
        t('materialPlating', language),
        t('tightTolerance', language),
        t('volumeProduction', language)
      ],
      image: assets.services_stamping || stampingServices
    },
    {
      icon: <Settings className={`w-12 h-12 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      title: t('moldDevelopment', language),
      desc: t('moldDevelopmentDesc', language),
      features: [
        t('cadcadDesign', language),
        t('moldFlow', language),
        t('rapidPrototyping', language),
        t('maintenanceRepair', language)
      ],
      image: assets.services_molddev || moldDevelopment
    },
    {
      icon: <Zap className={`w-12 h-12 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      title: t('injectionMolding', language),
      desc: t('injectionMoldingDesc', language),
      features: [
        t('multiCavity', language),
        t('materialEngineering', language),
        t('precisionTolerances', language),
        t('highVolume', language)
      ],
      image: assets.services_injection || injectionMolding
    },
    {
      icon: <Factory className={`w-12 h-12 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      title: t('cableAssembly', language),
      desc: t('cableAssemblyDesc', language),
      features: [
        t('customWire', language),
        t('connectorAssembly', language),
        t('testingValidation', language),
        t('ulCertification', language)
      ],
      image: assets.services_cable || cableAssembly
    },
    {
      icon: <CheckCircle className={`w-12 h-12 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      title: t('qualityControl', language),
      desc: t('qualityControlDesc', language),
      features: [
        t('incomingInspection', language),
        t('inProcessControl', language),
        t('finalTesting', language),
        t('documentationCertification', language)
      ],
      image: assets.services_qc || qualityControl
    }
  ];

  const process = [
    { step: '01', title: t('consultation', language), desc: t('consultationDesc', language) },
    { step: '02', title: t('design', language), desc: t('designDesc', language) },
    { step: '03', title: t('tooling', language), desc: t('toolingDesc', language) },
    { step: '04', title: t('production', language), desc: t('productionDesc', language) },
    { step: '05', title: t('testing', language), desc: t('testingDesc', language) },
    { step: '06', title: t('delivery', language), desc: t('deliveryDesc', language) }
  ];

  // Process section with scroll animation
  const [processRef, processVisible] = useScrollAnimation(0.1);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      <Helmet>
        <title>{t('ourServices', language)} - Qingshun Electronics</title>
        <meta name="description" content={t('servicesDescription', language)} />
        <meta name="keywords" content="connector manufacturing, injection molding, stamping services, mold development, cable assembly, quality control, custom manufacturing" />
        <meta name="author" content="Qingshun Electronics Technology Co., Ltd." />
        <link rel="canonical" href="https://qsconn.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://qsconn.com/services" />
        <meta property="og:title" content={`${t('ourServices', language)} | Custom Connector Manufacturing, Injection Molding & Cable Assembly - Qingshun Electronics`} />
        <meta property="og:description" content={t('servicesDescription', language)} />
        <meta property="og:image" content="https://qsconn.com/logo512.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://qsconn.com/services" />
        <meta property="twitter:title" content={`${t('ourServices', language)} | Qingshun Electronics - Connector Manufacturer in China`} />
        <meta property="twitter:description" content={t('servicesDescription', language)} />
        <meta property="twitter:image" content="https://qsconn.com/logo512.png" />
        <meta property="twitter:image:alt" content={`${t('ourServices', language)} - Qingshun Electronics`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": t('electronicsManufacturing', language),
            "name": "Custom Connector Manufacturing Services",
            "description": t('servicesDescription', language),
            "provider": {
              "@type": "Organization",
              "name": "Qingshun Electronics Technology Co., Ltd.",
              "url": "https://qsconn.com/"
            },
            "areaServed": "Worldwide"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://qsconn.com/"},
              {"@type": "ListItem", "position": 2, "name": t('ourServices', language), "item": "https://qsconn.com/services"}
            ]
          })}
        </script>
      </Helmet>

      <PageHeader
        theme={theme}
        badge={t('ourServices', language)}
        title={t('ourServices', language)}
        subtitle={t('comprehensiveServices', language)}
      />

      {/* Services Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div 
                key={idx} 
                className={`rounded-2xl p-8 shadow-lg transform hover:-translate-y-2 transition-all duration-300 hover:bg-gradient-to-br ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600 from-red-900/10 to-red-900/5' : 'bg-white border-2 border-gray-300 hover:border-red-600 from-red-50 to-red-100'}`}
                style={{
                  animation: `fadeInUp 0.6s ease-out ${idx * 0.1}s both`,
                }}
              >
                <div className="mb-4 rounded-xl overflow-hidden">
                  <div className={`relative rounded-xl overflow-hidden transition-all duration-300 ${theme === 'dark' ? 'border-2 border-red-900/50' : 'border-2 border-red-500'}`}>
                    <img 
                      src={service.image} 
                      alt={`${service.title} ${t('service', language)}`} 
                      className="w-full h-40 transition-transform duration-500 hover:scale-105 object-cover transform hover:scale-110 transition duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t transition-colors duration-300 ${theme === 'dark' ? 'from-black/70' : 'from-white/70'} to-transparent`}></div>
                  </div>
                </div>
                <div className="mb-4 text-2xl">{service.icon}</div>
                <h3 className={`text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>{service.title}</h3>
                <p className="mb-6">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start">
                      <ChevronRight className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="wave" />

      {/* Process */}
      <section 
        ref={processRef}
        className="py-20 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('ourProcessTitle', language)}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
              {t('fromConcept', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((item, idx) => (
              <div 
                key={idx} 
                className={`rounded-2xl p-8 shadow-lg relative overflow-hidden transform hover:-translate-y-1 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600' : 'bg-white border-2 border-gray-300 hover:border-red-600'}`}
                style={{
                  animation: processVisible ? `fadeInUp 0.5s ease-out ${idx * 0.1}s both` : 'none',
                }}
              >
                <div className={`absolute top-4 right-4 text-6xl font-bold transition-colors duration-300 ${theme === 'dark' ? 'text-red-900/20' : 'text-red-200'}`}>{item.step}</div>
                <div className="relative z-10">
                  <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'} mb-3 transition-colors duration-300`}>{item.step}</div>
                  <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-gray-900'}`}>{item.title}</h3>
                  <p className={`transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="dots" />

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`rounded-3xl p-12 shadow-lg relative overflow-hidden transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-red-900/50' : 'bg-white border-2 border-red-200'}`}>
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('readyToStartProject', language)}
            </h2>
            <p className={`text-xl mb-8 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
              {t('contactUsDiscuss', language)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-4 rounded-xl text-lg font-semibold text-white hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-500/30 inline-flex items-center justify-center transform hover:scale-105 duration-300">
                {t('getInTouch', language)}
                <ChevronRight className="ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Services;